<template>
  <div class="product-search">
    <div class="search-container">
      <!-- Search Input -->
      <div class="search-input-group">
        <div class="search-input-wrapper">
          <Icon name="🔍" class="search-icon" />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="searchPlaceholder"
            :disabled="disabled"
            @input="onSearchInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown="onKeyDown"
            autocomplete="off"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="clear-button"
            @click="clearSearch"
            :disabled="disabled"
          >
            <Icon name="✕" />
          </button>
        </div>
        
        <!-- Search Type Toggle -->
        <div class="search-type-toggle">
          <button
            v-for="type in searchTypes"
            :key="type.value"
            type="button"
            :class="['search-type-btn', { 'active': selectedSearchType === type.value }]"
            @click="setSearchType(type.value)"
            :disabled="disabled"
            :title="type.description"
          >
            {{ type.label }}
          </button>
        </div>
      </div>

      <!-- Search Results Dropdown -->
      <div
        v-if="showResults && (searchResults.length > 0 || suggestions.length > 0 || loading)"
        class="search-results-dropdown"
      >
        <!-- Loading State -->
        <div v-if="loading" class="search-loading">
          <Icon name="⏳" class="loading-icon" />
          <span>Searching...</span>
        </div>

        <!-- Suggestions -->
        <div v-else-if="suggestions.length > 0 && !searchQuery" class="search-suggestions">
          <div class="suggestions-header">
            <Icon name="💡" />
            <span>Suggestions</span>
          </div>
          <div
            v-for="(suggestion, index) in suggestions"
            :key="'suggestion-' + index"
            :class="['suggestion-item', { 'highlighted': highlightedIndex === index }]"
            @click="selectSuggestion(suggestion)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="suggestion-icon">
              <Icon :name="getSuggestionIcon(suggestion.type)" />
            </div>
            <div class="suggestion-content">
              <span class="suggestion-label">{{ suggestion.label }}</span>
              <span class="suggestion-type">{{ suggestion.type.toUpperCase() }}</span>
            </div>
          </div>
        </div>

        <!-- Search Results -->
        <div v-else-if="searchResults.length > 0" class="search-results">
          <div class="results-header">
            <Icon name="📦" />
            <span>{{ searchResults.length }} result{{ searchResults.length !== 1 ? 's' : '' }} found</span>
          </div>
          <div
            v-for="(product, index) in searchResults"
            :key="'result-' + product.id"
            :class="['result-item', { 'highlighted': highlightedIndex === index }]"
            @click="selectProduct(product)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="result-info">
              <div class="product-name">
                {{ product.name }}
              </div>
              <div class="product-identifiers">
                <span class="product-sku">SKU: {{ product.sku }}</span>
                <span v-if="product.barcode" class="product-barcode">
                  Barcode: {{ product.barcode }}
                </span>
              </div>
              <div class="product-meta">
                <span v-if="product.globalProduct" class="global-product-status">
                  <Icon :name="product.globalProduct.status === 'VALID' ? '✅' : '⚠️'" />
                  {{ product.globalProduct.status }}
                </span>
                <span class="product-type">
                  {{ product.hasGlobalProduct ? 'Global' : 'Manual' }}
                </span>
              </div>
            </div>
            <div class="result-actions">
              <button
                type="button"
                class="select-button"
                @click.stop="selectProduct(product)"
              >
                Select
              </button>
            </div>
          </div>
        </div>

        <!-- No Results -->
        <div v-else-if="searchQuery && !loading" class="no-results">
          <Icon name="🔍" class="no-results-icon" />
          <div class="no-results-content">
            <p>No products found for "{{ searchQuery }}"</p>
            <p class="no-results-suggestions">
              Try searching by:
              <span class="search-tips">
                <span>• SKU (e.g., "COCA_ZERO_1")</span>
                <span>• Barcode (e.g., "1234567890")</span>
                <span>• Product name (e.g., "Coca Cola")</span>
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- Search Filters -->
      <div v-if="showFilters" class="search-filters">
        <div class="filter-group">
          <label class="filter-label">
            <input
              type="checkbox"
              v-model="filters.hasBarcode"
              @change="onFilterChange"
            />
            <span>Has Barcode</span>
          </label>
          <label class="filter-label">
            <input
              type="checkbox"
              v-model="filters.hasGlobalProduct"
              @change="onFilterChange"
            />
            <span>Linked to Global Product</span>
          </label>
          <label class="filter-label">
            <input
              type="checkbox"
              v-model="filters.validOnly"
              @change="onFilterChange"
            />
            <span>Valid Status Only</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Recent Searches -->
    <div v-if="showRecentSearches && recentSearches.length > 0" class="recent-searches">
      <div class="recent-searches-header">
        <Icon name="🕒" />
        <span>Recent Searches</span>
      </div>
      <div class="recent-searches-list">
        <button
          v-for="(search, index) in recentSearches"
          :key="'recent-' + index"
          type="button"
          class="recent-search-item"
          @click="selectRecentSearch(search)"
        >
          {{ search }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import Icon from '@/components/Icon.vue'
import api from '@/services/api'

export default {
  name: 'ProductSearch',
  components: {
    Icon
  },
  props: {
    placeholder: {
      type: String,
      default: 'Search by SKU, barcode, or product name...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    showRecentSearches: {
      type: Boolean,
      default: true
    },
    maxResults: {
      type: Number,
      default: 10
    },
    searchType: {
      type: String,
      default: 'all' // 'all', 'sku', 'barcode', 'name'
    },
    autoFocus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['select', 'search', 'clear'],
  setup(props, { emit }) {
    const searchInput = ref(null)
    const searchQuery = ref('')
    const selectedSearchType = ref(props.searchType)
    const searchResults = ref([])
    const suggestions = ref([])
    const loading = ref(false)
    const showResults = ref(false)
    const highlightedIndex = ref(-1)
    const recentSearches = ref([])
    const searchTimeout = ref(null)

    // Search filters
    const filters = ref({
      hasBarcode: false,
      hasGlobalProduct: false,
      validOnly: false
    })

    // Search types configuration
    const searchTypes = [
      { value: 'all', label: 'All', description: 'Search across all fields' },
      { value: 'sku', label: 'SKU', description: 'Search by SKU only' },
      { value: 'barcode', label: 'Barcode', description: 'Search by barcode only' },
      { value: 'name', label: 'Name', description: 'Search by product name only' }
    ]

    // Computed properties
    const searchPlaceholder = computed(() => {
      switch (selectedSearchType.value) {
        case 'sku':
          return 'Search by SKU (e.g., COCA_ZERO_1)...'
        case 'barcode':
          return 'Search by barcode (e.g., 1234567890)...'
        case 'name':
          return 'Search by product name...'
        default:
          return props.placeholder
      }
    })

    // Load recent searches from localStorage
    const loadRecentSearches = () => {
      try {
        const saved = localStorage.getItem('product-search-recent')
        if (saved) {
          recentSearches.value = JSON.parse(saved).slice(0, 5)
        }
      } catch (error) {
        console.error('Error loading recent searches:', error)
      }
    }

    // Save recent searches to localStorage
    const saveRecentSearches = () => {
      try {
        localStorage.setItem('product-search-recent', JSON.stringify(recentSearches.value))
      } catch (error) {
        console.error('Error saving recent searches:', error)
      }
    }

    // Add to recent searches
    const addToRecentSearches = (query) => {
      if (query && query.trim().length > 0) {
        const cleanQuery = query.trim()
        recentSearches.value = [
          cleanQuery,
          ...recentSearches.value.filter(s => s !== cleanQuery)
        ].slice(0, 5)
        saveRecentSearches()
      }
    }

    // Get search suggestions
    const getSuggestions = async () => {
      if (!searchQuery.value || searchQuery.value.length < 2) {
        suggestions.value = []
        return
      }

      try {
        const response = await api.get('/search/suggestions', {
          params: {
            q: searchQuery.value,
            type: selectedSearchType.value === 'all' ? undefined : selectedSearchType.value,
            limit: 5
          }
        })
        suggestions.value = response.data.suggestions || []
      } catch (error) {
        console.error('Error getting suggestions:', error)
        suggestions.value = []
      }
    }

    // Perform search
    const performSearch = async () => {
      if (!searchQuery.value || searchQuery.value.length < 2) {
        searchResults.value = []
        return
      }

      loading.value = true
      try {
        const params = {
          q: searchQuery.value,
          type: selectedSearchType.value,
          limit: props.maxResults,
          fuzzy: 'true'
        }

        // Add filters
        if (filters.value.hasBarcode) params.hasBarcode = 'true'
        if (filters.value.hasGlobalProduct) params.hasGlobalProduct = 'true'
        if (filters.value.validOnly) params.status = 'VALID'

        const response = await api.get('/search/products', { params })
        searchResults.value = response.data.products || []
        
        // Add to recent searches
        addToRecentSearches(searchQuery.value)
        
        // Emit search event
        emit('search', {
          query: searchQuery.value,
          type: selectedSearchType.value,
          results: searchResults.value
        })
      } catch (error) {
        console.error('Error performing search:', error)
        searchResults.value = []
      } finally {
        loading.value = false
      }
    }

    // Handle search input
    const onSearchInput = () => {
      highlightedIndex.value = -1
      
      // Clear previous timeout
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
      
      // Debounced search
      searchTimeout.value = setTimeout(() => {
        if (searchQuery.value.length >= 2) {
          performSearch()
        } else {
          searchResults.value = []
          getSuggestions()
        }
      }, 300)
    }

    // Handle focus
    const onFocus = () => {
      showResults.value = true
      if (!searchQuery.value) {
        getSuggestions()
      }
    }

    // Handle blur
    const onBlur = () => {
      // Delay hiding results to allow clicking on them
      setTimeout(() => {
        showResults.value = false
      }, 200)
    }

    // Handle keyboard navigation
    const onKeyDown = (event) => {
      const resultCount = searchResults.value.length || suggestions.value.length
      
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          highlightedIndex.value = Math.min(highlightedIndex.value + 1, resultCount - 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1)
          break
        case 'Enter':
          event.preventDefault()
          if (highlightedIndex.value >= 0) {
            if (searchResults.value.length > 0) {
              selectProduct(searchResults.value[highlightedIndex.value])
            } else if (suggestions.value.length > 0) {
              selectSuggestion(suggestions.value[highlightedIndex.value])
            }
          }
          break
        case 'Escape':
          showResults.value = false
          searchInput.value?.blur()
          break
      }
    }

    // Select a product
    const selectProduct = (product) => {
      emit('select', product)
      showResults.value = false
      searchQuery.value = product.name
    }

    // Select a suggestion
    const selectSuggestion = (suggestion) => {
      searchQuery.value = suggestion.value
      performSearch()
    }

    // Select recent search
    const selectRecentSearch = (search) => {
      searchQuery.value = search
      performSearch()
    }

    // Clear search
    const clearSearch = () => {
      searchQuery.value = ''
      searchResults.value = []
      suggestions.value = []
      showResults.value = false
      emit('clear')
    }

    // Set search type
    const setSearchType = (type) => {
      selectedSearchType.value = type
      if (searchQuery.value) {
        performSearch()
      }
    }

    // Handle filter changes
    const onFilterChange = () => {
      if (searchQuery.value) {
        performSearch()
      }
    }

    // Get suggestion icon
    const getSuggestionIcon = (type) => {
      switch (type) {
        case 'sku':
          return '🏷️'
        case 'barcode':
          return '📊'
        case 'name':
          return '📦'
        default:
          return '🔍'
      }
    }

    // Lifecycle
    onMounted(() => {
      loadRecentSearches()
      if (props.autoFocus) {
        nextTick(() => {
          searchInput.value?.focus()
        })
      }
    })

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    return {
      searchInput,
      searchQuery,
      selectedSearchType,
      searchResults,
      suggestions,
      loading,
      showResults,
      highlightedIndex,
      recentSearches,
      filters,
      searchTypes,
      searchPlaceholder,
      onSearchInput,
      onFocus,
      onBlur,
      onKeyDown,
      selectProduct,
      selectSuggestion,
      selectRecentSearch,
      clearSearch,
      setSearchType,
      onFilterChange,
      getSuggestionIcon
    }
  }
}
</script>

<style scoped>
.product-search {
  position: relative;
  width: 100%;
  max-width: 600px;
}

.search-container {
  position: relative;
}

.search-input-group {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 0.875rem 2.5rem 0.875rem 2.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-input:disabled {
  background: #f7fafc;
  cursor: not-allowed;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #718096;
  font-size: 1.125rem;
}

.clear-button {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  font-size: 1.125rem;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.clear-button:hover {
  color: #718096;
  background: #f7fafc;
}

.search-type-toggle {
  display: flex;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.search-type-btn {
  background: white;
  border: none;
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-right: 1px solid #e2e8f0;
}

.search-type-btn:last-child {
  border-right: none;
}

.search-type-btn:hover {
  background: #f7fafc;
}

.search-type-btn.active {
  background: #667eea;
  color: white;
}

.search-type-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.search-results-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
}

.search-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #718096;
}

.loading-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.search-suggestions,
.search-results {
  padding: 0.5rem 0;
}

.suggestions-header,
.results-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 1px solid #e2e8f0;
}

.suggestion-item,
.result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f7fafc;
}

.suggestion-item:hover,
.result-item:hover,
.suggestion-item.highlighted,
.result-item.highlighted {
  background: #f7fafc;
}

.suggestion-item:last-child,
.result-item:last-child {
  border-bottom: none;
}

.suggestion-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.suggestion-content {
  flex: 1;
}

.suggestion-label {
  display: block;
  font-weight: 500;
  color: #2d3748;
}

.suggestion-type {
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
  font-weight: 600;
}

.result-info {
  flex: 1;
}

.product-name {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.product-identifiers {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.25rem;
}

.product-sku,
.product-barcode {
  font-size: 0.875rem;
  color: #718096;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
}

.global-product-status {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  background: #f7fafc;
  font-weight: 500;
}

.product-type {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  background: #e2e8f0;
  color: #4a5568;
  font-weight: 500;
}

.result-actions {
  flex-shrink: 0;
}

.select-button {
  background: #667eea;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-button:hover {
  background: #5a67d8;
}

.no-results {
  padding: 2rem;
  text-align: center;
  color: #718096;
}

.no-results-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.no-results-content p {
  margin: 0 0 1rem 0;
}

.no-results-suggestions {
  font-size: 0.875rem;
}

.search-tips {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
  text-align: left;
}

.search-filters {
  margin-top: 0.5rem;
}

.filter-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
  cursor: pointer;
}

.filter-label input[type="checkbox"] {
  margin: 0;
}

.recent-searches {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.recent-searches-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.5rem;
}

.recent-searches-list {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.recent-search-item {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recent-search-item:hover {
  background: #e2e8f0;
  border-color: #cbd5e0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-input-group {
    flex-direction: column;
  }
  
  .search-type-toggle {
    justify-content: center;
  }
  
  .search-type-btn {
    flex: 1;
    text-align: center;
  }
  
  .product-identifiers {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .filter-group {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .recent-searches-list {
    flex-direction: column;
  }
}
</style> 